import React, { useEffect, useMemo, useState } from 'react';
import { Bot, Cpu, Gauge, Wrench } from 'lucide-react';
import { llmGatewayApi } from '../../llm-gateway/llmGatewayApi';
import type { LlmGateway } from '../../llm-gateway/llmGatewayTypes';
import { FormSection, FormStack, GatewayDrawer, SearchableSelect, fieldClass, ghostBtn, helperClass, labelClass, panelClass, primaryBtn, selectField } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { McpCapabilityKind, PublishedCapability, QuotaAssignmentStatus, QuotaKind, QuotaListingTab, QuotaScope } from '../governedMcpTypes';
import { currentQuotaMonth, findQuotaAssignment, formatQuotaMonth, isQuotaMonth, resourceTypeFromQuotaKind } from '../quotaUsage';

const SCOPE_LABEL: Record<QuotaListingTab, string> = {
  client: 'Client',
  department: 'Department',
  role: 'Role',
  user: 'User',
};

type QuotaType = QuotaKind;

const QUOTA_TYPES: {
  id: QuotaType;
  label: string;
  title: string;
  purpose: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}[] = [
  {
    id: 'client',
    label: 'Client',
    title: 'Client Quotas',
    purpose: 'Control consumption by applications, copilots, or clients.',
    icon: Bot,
  },
  {
    id: 'tool',
    label: 'Tools',
    title: 'MCP Tool Quotas',
    purpose: 'Control tool execution and external API usage.',
    icon: Wrench,
  },
  {
    id: 'llm',
    label: 'LLM Gateway',
    title: 'LLM Gateway Quotas',
    purpose: 'Control model inference through the gateway.',
    icon: Cpu,
  },
];

function capabilityOf(item: PublishedCapability): McpCapabilityKind {
  if (item.mcpCapability) return item.mcpCapability;
  const hasTool = item.mcpRepresentations?.includes('tool');
  const hasResource = item.mcpRepresentations?.includes('resource');
  if (hasResource && !hasTool) return 'resource';
  return 'tool';
}

function parseLimit(value: string): number {
  return Number(value.replace(/[$,]/g, '').trim());
}

function requireNonNegative(value: string, label: string): { ok: true; value: number } | { ok: false; reason: string } {
  if (!value.trim()) {
    return { ok: false, reason: `${label} is required.` };
  }
  const parsed = parseLimit(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { ok: false, reason: `${label} must be zero or a positive number.` };
  }
  return { ok: true, value: parsed };
}

function LimitField({
  label,
  value,
  onChange,
  placeholder,
  isLight,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  isLight: boolean;
}) {
  return (
    <div>
      <label className={labelClass(isLight)}>{label}</label>
      <input
        className={`${fieldClass(isLight)} tabular-nums`}
        inputMode="numeric"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function ClientLimitStack({
  requestLimit,
  tokenLimit,
  spendLimit,
  onRequestChange,
  onTokenChange,
  onSpendChange,
  isLight,
}: {
  requestLimit: string;
  tokenLimit: string;
  spendLimit: string;
  onRequestChange: (value: string) => void;
  onTokenChange: (value: string) => void;
  onSpendChange: (value: string) => void;
  isLight: boolean;
}) {
  const rows = [
    { label: 'Monthly request limit', value: requestLimit, onChange: onRequestChange, placeholder: '25000' },
    { label: 'Monthly token limit', value: tokenLimit, onChange: onTokenChange, placeholder: '500000' },
    { label: 'Monthly spending limit', value: spendLimit, onChange: onSpendChange, placeholder: '0.00' },
  ];
  return (
    <div className={`${panelClass(isLight)} overflow-hidden divide-y ${isLight ? 'divide-neutral-100' : 'divide-neutral-800'}`}>
      {rows.map((row) => (
        <div key={row.label} className="px-4 py-3.5">
          <label className={labelClass(isLight)}>{row.label}</label>
          <input
            className={`${fieldClass(isLight)} tabular-nums`}
            inputMode="numeric"
            placeholder={row.placeholder}
            value={row.value}
            onChange={(event) => row.onChange(event.target.value)}
          />
        </div>
      ))}
    </div>
  );
}

export default function QuotaAssignDrawer({
  open,
  listing,
  quotaKind = 'client',
  clientId,
  month,
  isLight,
  onClose,
  onAssigned,
  onToast,
}: {
  open: boolean;
  listing: QuotaListingTab;
  quotaKind?: QuotaKind;
  clientId: string;
  month: string;
  isLight: boolean;
  onClose: () => void;
  onAssigned?: (next: { listing: QuotaListingTab; month: string; quotaKind: QuotaKind }) => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory, setQuotaAssignment } = useGovernedMcp();
  const [quotaType, setQuotaType] = useState<QuotaType>('client');
  const [formClientId, setFormClientId] = useState('');
  const [toolId, setToolId] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [formMonth, setFormMonth] = useState(currentQuotaMonth());
  const [tokenLimit, setTokenLimit] = useState('');
  const [requestLimit, setRequestLimit] = useState('');
  const [spendLimit, setSpendLimit] = useState('');
  const [maxToolCalls, setMaxToolCalls] = useState('');
  const [status, setStatus] = useState<QuotaAssignmentStatus>('active');
  const [renewsMonthly, setRenewsMonthly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);

  const selectedType = QUOTA_TYPES.find((item) => item.id === quotaType) ?? QUOTA_TYPES[0];

  const keyUsers = useMemo(() => {
    const seen = new Set<string>();
    return state.apiKeys
      .map((key) => {
        const id = key.userId.trim();
        if (!id || seen.has(id)) return null;
        seen.add(id);
        const dirUser = quotaDirectory.users.find((item) => item.id === id);
        return {
          id,
          name: dirUser?.name || key.name || key.username || id,
          hint: dirUser?.email || key.email || '',
        };
      })
      .filter((item): item is { id: string; name: string; hint: string } => Boolean(item));
  }, [quotaDirectory.users, state.apiKeys]);

  const directoryUsers = useMemo(
    () =>
      quotaDirectory.users.map((item) => ({
        id: item.id,
        label: item.name,
        hint: item.email || item.roleName || '',
      })),
    [quotaDirectory.users]
  );

  const options =
    listing === 'department'
      ? quotaDirectory.departments.map((item) => ({ id: item.id, label: item.name }))
      : listing === 'role'
        ? quotaDirectory.roles.map((item) => ({ id: item.id, label: item.name }))
        : directoryUsers.length
          ? directoryUsers
          : keyUsers.map((item) => ({ id: item.id, label: item.name, hint: item.hint }));

  const toolOptions = useMemo(
    () =>
      state.published
        .filter((item) => capabilityOf(item) === 'tool')
        .map((item) => ({
          id: item.id,
          label: item.mcpToolName || item.sourceName || item.id,
          hint: item.sourceName && item.mcpToolName && item.sourceName !== item.mcpToolName ? item.sourceName : item.description,
        })),
    [state.published]
  );

  const gatewayOptions = useMemo(
    () =>
      gateways.map((item) => ({
        id: item.id,
        label: item.name || item.id,
        hint: [item.publicAlias, item.llmType, item.status].filter(Boolean).join(' · '),
      })),
    [gateways]
  );

  useEffect(() => {
    if (!open) return;
    const nextClient = clientId !== 'all' ? clientId : state.clients[0]?.id || '';
    const nextMonth = isQuotaMonth(month) ? month : currentQuotaMonth();
    setQuotaType(quotaKind);
    setFormClientId(nextClient);
    setToolId('');
    setGatewayId('');
    setFormMonth(nextMonth);
    setScopeId('');
    setTokenLimit('');
    setRequestLimit('');
    setSpendLimit('');
    setMaxToolCalls('');
    setStatus('active');
    setRenewsMonthly(false);
    setError(null);
  }, [clientId, month, open, quotaKind, state.clients]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setGateways([]);
    setGatewaysError(null);
    setGatewaysLoading(true);
    logGovernedMcp('app', 'quota_llm_gateways_load');
    llmGatewayApi
      .listGateways()
      .then((items) => {
        if (cancelled) return;
        setGateways(items);
        logGovernedMcp('app', 'quota_llm_gateways_loaded', { count: items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load LLM gateways.';
        setGatewaysError(message);
        logGovernedMcp('error', 'quota_llm_gateways_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setGatewaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selected = options.find((item) => item.id === scopeId);
  const persistClientId = quotaType === 'client' ? formClientId : clientId !== 'all' ? clientId : state.clients[0]?.id || '';
  const existing =
    listing !== 'client' && persistClientId && scopeId && isQuotaMonth(formMonth)
      ? findQuotaAssignment(state.quotaAssignments, listing, scopeId, persistClientId, formMonth)
      : undefined;

  useEffect(() => {
    if (!open) return;
    setTokenLimit(existing ? String(existing.tokenLimit) : '');
    setStatus(existing?.status === 'inactive' ? 'inactive' : 'active');
    setRenewsMonthly(Boolean(existing?.renewsMonthly));
  }, [existing, open, persistClientId, formMonth, scopeId]);

  const fail = (reason: string) => {
    setError(reason);
    logGovernedMcp('warning', 'quota_assign_invalid', { quotaType, reason });
    onToast('error', reason);
  };

  const finish = (label: string) => {
    onAssigned?.({ listing, month: formMonth, quotaKind: quotaType });
    onToast('success', `Quota assigned for ${label} · ${formatQuotaMonth(formMonth)}.`);
    onClose();
  };

  const persistTokenAssignment = () => {
    if (listing === 'client') {
      fail('Select a role, department, or user before assigning a quota.');
      return false;
    }
    const parsed = parseLimit(tokenLimit);
    const result = setQuotaAssignment(
      listing,
      scopeId,
      persistClientId,
      parsed,
      selected?.label,
      formMonth,
      status,
      renewsMonthly,
      resourceTypeFromQuotaKind(quotaType)
    );
    if (!result.ok) {
      fail(result.reason);
      return false;
    }
    return true;
  };

  const submit = () => {
    if (quotaType === 'tool') {
      if (listing !== 'client' && !scopeId.trim()) {
        fail('A department, role, or user is required.');
        return;
      }
      if (!toolId.trim()) {
        fail('Select a published tool.');
        return;
      }
      if (!isQuotaMonth(formMonth)) {
        fail('Select a month.');
        return;
      }
      const toolCalls = requireNonNegative(maxToolCalls, 'Max tool calls');
      if (!toolCalls.ok) {
        fail(toolCalls.reason);
        return;
      }
      const tool = toolOptions.find((item) => item.id === toolId);
      if (listing === 'client') {
        fail('A department, role, or user is required.');
        return;
      }
      const saved = setQuotaAssignment(
        listing,
        scopeId,
        toolId,
        toolCalls.value,
        tool?.label || selected?.label,
        formMonth,
        status,
        renewsMonthly,
        'mcp_tool'
      );
      if (!saved.ok) {
        fail(saved.reason);
        return;
      }
      logGovernedMcp('app', 'quota_tool_assignment_saved', {
        scope: listing,
        scopeId,
        toolId,
        toolName: tool?.label || null,
        month: formMonth,
        maxToolCalls: toolCalls.value,
        status,
      });
      finish(selected?.label || tool?.label || SCOPE_LABEL[listing]);
      return;
    }

    if (quotaType === 'llm') {
      if (!gatewayId.trim()) {
        fail('Select an LLM gateway.');
        return;
      }
      if (listing === 'client') {
        fail('A department, role, or user is required.');
        return;
      }
      const gateway = gatewayOptions.find((item) => item.id === gatewayId);
      const parsed = parseLimit(tokenLimit);
      const saved = setQuotaAssignment(
        listing,
        scopeId,
        gatewayId,
        parsed,
        gateway?.label || selected?.label,
        formMonth,
        status,
        renewsMonthly,
        'llm_gateway'
      );
      if (!saved.ok) {
        fail(saved.reason);
        return;
      }
      logGovernedMcp('app', 'quota_llm_assignment_saved', {
        scope: listing,
        scopeId,
        gatewayId,
        month: formMonth,
        tokenLimit: parsed,
        status,
      });
      finish(selected?.label || gateway?.label || SCOPE_LABEL[listing]);
      return;
    }

    if (!persistTokenAssignment()) return;

    logGovernedMcp('app', 'quota_assignment_submitted', {
      quotaType,
      scope: listing,
      scopeId,
      clientId: persistClientId,
      month: formMonth,
      tokenLimit: parseLimit(tokenLimit),
      requestLimit: requestLimit.trim() || null,
      spendLimit: spendLimit.trim() || null,
      status,
      renewsMonthly,
    });
    finish(selected?.label || SCOPE_LABEL[listing]);
  };

  return (
    <GatewayDrawer
      open={open}
      title="Assign quota"
      subtitle={SCOPE_LABEL[listing]}
      eyebrow="Governed MCP / Quotas & Spendings"
      icon={<Gauge size={18} />}
      isLight={isLight}
      onClose={onClose}
      size="lg"
      maximizable
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            Assign quota
          </button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <FormSection title="Assignment" description={selectedType.purpose} isLight={isLight}>
          <div className="space-y-4">
            <div>
              <label className={labelClass(isLight)}>Quota type</label>
              <select
                className={`${selectField(isLight)} w-full`}
                value={quotaType}
                onChange={(event) => {
                  const next = event.target.value as QuotaType;
                  setQuotaType(next);
                  setError(null);
                  logGovernedMcp('app', 'quota_type_selected', { quotaType: next });
                }}
              >
                {QUOTA_TYPES.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.label}
                  </option>
                ))}
              </select>
              <p className={helperClass(isLight)}>{selectedType.purpose}</p>
            </div>

            {quotaType === 'client' ? (
              <div>
                <label className={labelClass(isLight)}>Client</label>
                <select className={`${selectField(isLight)} w-full`} value={formClientId} onChange={(event) => setFormClientId(event.target.value)}>
                  <option value="">Select a client</option>
                  {state.clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {quotaType === 'tool' ? (
              <div>
                <SearchableSelect
                  label="Tool"
                  value={toolId}
                  options={toolOptions}
                  onChange={setToolId}
                  isLight={isLight}
                  placeholder={toolOptions.length ? 'Select a published tool' : 'No published tools'}
                  searchPlaceholder="Search tools…"
                />
                {!toolOptions.length ? <p className={helperClass(isLight)}>No published tools for this tenant.</p> : null}
              </div>
            ) : null}

            {quotaType === 'llm' ? (
              <div>
                <SearchableSelect
                  label="LLM gateway"
                  value={gatewayId}
                  options={gatewayOptions}
                  onChange={setGatewayId}
                  isLight={isLight}
                  placeholder={gatewaysLoading ? 'Loading gateways…' : gatewayOptions.length ? 'Select an LLM gateway' : 'No LLM gateways'}
                  searchPlaceholder="Search gateways…"
                />
                {gatewaysError ? <p className="mt-1 text-xs text-red-500">{gatewaysError}</p> : null}
                {!gatewaysLoading && !gatewaysError && !gatewayOptions.length ? (
                  <p className={helperClass(isLight)}>No LLM gateways for this tenant.</p>
                ) : null}
              </div>
            ) : null}

            {listing !== 'client' ? (
              <SearchableSelect
                label={SCOPE_LABEL[listing]}
                value={scopeId}
                options={options}
                onChange={setScopeId}
                isLight={isLight}
                placeholder={`Select a ${SCOPE_LABEL[listing].toLowerCase()}`}
                searchPlaceholder={listing === 'user' ? 'Search name or email…' : `Search ${SCOPE_LABEL[listing].toLowerCase()}s…`}
              />
            ) : null}
            <div>
              <label className={labelClass(isLight)}>Month</label>
              <input
                type="month"
                className={fieldClass(isLight)}
                value={formMonth}
                onChange={(event) => setFormMonth(event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass(isLight)}>Status</label>
              <select
                className={`${selectField(isLight)} w-full`}
                value={status}
                onChange={(event) => setStatus(event.target.value as QuotaAssignmentStatus)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <label className={`flex items-start gap-3 rounded-xl border px-3 py-3 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
              <input
                type="checkbox"
                className="mt-1"
                checked={renewsMonthly}
                onChange={(event) => setRenewsMonthly(event.target.checked)}
              />
              <span>
                <span className={`block text-sm font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>Renews monthly</span>
                <span className={helperClass(isLight)}>
                  The limit resets automatically at the start of each month until you replace it.
                </span>
              </span>
            </label>

            {quotaType === 'tool' ? (
              <LimitField label="Max tool calls" value={maxToolCalls} onChange={setMaxToolCalls} placeholder="10000" isLight={isLight} />
            ) : quotaType === 'llm' ? (
              <LimitField label="Monthly token limit" value={tokenLimit} onChange={setTokenLimit} placeholder="500000" isLight={isLight} />
            ) : (
              <ClientLimitStack
                requestLimit={requestLimit}
                tokenLimit={tokenLimit}
                spendLimit={spendLimit}
                onRequestChange={setRequestLimit}
                onTokenChange={setTokenLimit}
                onSpendChange={setSpendLimit}
                isLight={isLight}
              />
            )}
          </div>
        </FormSection>
      </FormStack>
    </GatewayDrawer>
  );
}
